import { c as _c } from "react/compiler-runtime";
import ErrorIcon from "@assets/illustrations/error_page.svg?react";
import { SphereSupportLink } from "@components/common/sphere-support-link";
import { UserMenu } from "@components/header/user-menu";
import { ContactSupportLocations } from "@custom-types/types";
import { Box } from "@mui-wrapper";
import { FontWeights, Text, Button, SphereXgLogo } from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect } from "react";

/**
 * This is a custom error component which is shown if something goes wrong in routing.
 * e.g. typing a route that is not available.
 */
export function CustomErrorBoundary() {
  const $ = _c(13);
  const {
    trackAsyncEvent
  } = useTrackEvent();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleRefresh() {
      window.location.reload();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleRefresh = t0;
  let t1;
  if ($[1] !== trackAsyncEvent) {
    t1 = () => {
      try {
        trackAsyncEvent({
          name: "encountered unexpected error"
        });
      } catch {
        throw new Error("Failed to track async event: encountered unexpected error.");
      }
    };
    $[1] = trackAsyncEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      backgroundColor: "surfaces.secondary",
      minHeight: "100vh"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      pt: "14px",
      pr: "24px",
      pl: "8px",
      pb: "16px",
      display: "flex",
      justifyContent: "space-between"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Box component="div" sx={t3}><Box><SphereXgLogo height="3.875rem" /></Box><UserMenu /></Box>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Box sx={{
      mt: "50px"
    }}><ErrorIcon width={400} height="auto" /></Box>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      mt: "37px",
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
      maxWidth: "700px"
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Text variant="heading32" sx={{
      fontSize: "38px",
      color: "text.secondary",
      fontWeight: FontWeights.SemiBold
    }}>Something went wrong</Text>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      fontSize: "20px",
      color: "text.secondary",
      fontWeight: FontWeights.Regular
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Box sx={t7}>{t8}<Text variant="bodyL" sx={t9}>We encountered an unexpected issue, please refresh the page. If the issue persists, contact{" "}<SphereSupportLink location={ContactSupportLocations.criticalErrorPage} />.</Text></Box>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Box sx={t2}>{t4}<Box sx={t5}>{t6}{t10}<Button sx={{
          my: "85px"
        }} onClick={handleRefresh}>Refresh</Button></Box></Box>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  return t11;
}